import moment from "moment";

require("moment/locale/fr"); // #region CLASS

/**
 * Utilitaire contenant des fonctions statiques pour les dates
 * @category Utils
 * @hideconstructor
 */


var DateUtils = /*#__PURE__*/function () {
  function DateUtils() {}

  /**
   * Fonction permettant d'ajouter des jours à une date
   * @param {date} dateRef La date de référence
   * @param {number} [countDay=1] Le nombre de jour à ajouter (peut être négatif)
   * @returns {date} La date avec _countDay_ jours en plus ou en moins
   */
  DateUtils.addDays = function addDays(dateRef, countDay) {
    if (countDay === void 0) {
      countDay = 1;
    }

    return moment.utc(dateRef).add(countDay, "d");
  };

  /**
   * Fonction permettant d'ajouter des heures à une date
   * @param {date} dateRef La date de référence
   * @param {number} [countHour=1] Le nombre d'heure à ajouter (peut être négatif)
   * @returns {date} La date avec _countHour_ heures en plus ou en moins
   */
  DateUtils.addHours = function addHours(dateRef, countHour) {
    if (countHour === void 0) {
      countHour = 1;
    }

    return moment(dateRef).add(countHour, "h");
  };

  /**
   * Fonction permettant d'ajouter des mois à une date
   * @param {date} dateRef La date de référence
   * @param {number} [countMonth=1] Le nombre de mois à ajouter (peut être négatif)
   * @returns {date} La date avec _countMonth_ mois en plus ou en moins
   */
  DateUtils.addMonth = function addMonth(dateRef, countMonth) {
    if (countMonth === void 0) {
      countMonth = 1;
    }

    return moment.utc(dateRef).add(countMonth, "M");
  };

  /**
   * Fonction permettant de calculer le nombre de jour entre 2 dates
   * @param {date} dateDebut La date de début
   * @param {date} dateFin La date de fin
   * @param {boolean} [includeDebut=false] Inclure la date de début
   * @returns {number} Le nombre de jour entre _dateDebut_ et _dateFin_
   */
  DateUtils.daysBetween = function daysBetween(dateDebut, dateFin, includeDebut) {
    if (includeDebut === void 0) {
      includeDebut = false;
    }

    var result = moment(dateFin).diff(dateDebut, "days");

    if (includeDebut) {
      result += 1;
    }

    return result;
  };

  /**
   * Fonction permettant de récupérer la date du 1er jour d'un mois
   * @param {date} dateRef La date de référence
   * @returns {date} La date du premier jour du mois correspondant à _dateRef_
   */
  DateUtils.firstDayOfMonth = function firstDayOfMonth(dateRef) {
    return moment.utc(dateRef).startOf("month").toDate();
  };

  /**
   * Fonction permettant de récupérer la date du 1er jour d'une semaine
   * @param {date} dateRef La date de référence
   * @returns {date} La date du premier jour de la semaine correspondant à _dateRef_
   */
  DateUtils.firstDayOfWeek = function firstDayOfWeek(dateRef) {
    return moment.utc(dateRef).startOf("week").toDate();
  };

  /**
   * Fonction permettant de récupérer la date au format JJ/MM/AAAA
   * @param {date} date La date à formatter
   * @returns {string} Chaine avec la date au format JJ/MM/AAAA
   */
  DateUtils.getDateDDMMYYYY = function getDateDDMMYYYY(date) {
    return moment.utc(date).format("DD/MM/YYYY");
  };

  /**
   * Fonction permettant de récupérer la date au format JJ/MM/AAAA HH:mm
   * @param {date} date  La date / heure à formatter
   * @returns {string} Chaine avec la date / heure au format JJ/MM/AAAA HH:mm
   */
  DateUtils.getDateHeure = function getDateHeure(date) {
    return moment(date).format("DD/MM/YYYY HH:mm");
  };

  /**
   * Fonction permettant de récupérer la date au format AAAA-MM-DD
   * @param {date} date La date à formatter
   * @returns {string} Chaine avec la date au format AAAA-MM-DD
   */
  DateUtils.getDateYYYYMMDD = function getDateYYYYMMDD(date) {
    return moment.utc(date).format("YYYY-MM-DD");
  };

  /**
   * Fonction permettant de récupérer la date du dernier jour d'un mois
   * @param {date} dateRef La date de référence
   * @returns {date} La date du dernier jour du mois correspondant à _dateRef_
   */
  DateUtils.getDayName = function getDayName(date) {
    return moment(date).format("dddd");
  };

  /**
   * Init de la locale pour la variable moment
   */
  DateUtils.initLocaleMoment = function initLocaleMoment() {
    moment.locale("fr");
  };

  /**
   * Fonction permettant de vérifier la validité d'une date
   * @param {date} date La date à formatter
   * @param {string} format Le format de la date
   * @returns {boolean} true si la date est valide, false sinon
   */
  DateUtils.isDateValid = function isDateValid(date, format) {
    return moment(date, format, true).isValid();
  };

  /**
   * Fonction permettant de vérifier si un jour donné est le dernier du mois (le dernier lundi du mois par exemple)
   * @param {date} date La date à tester
   * @returns {boolean} true si le jour donné est le dernier du mois
  */
  DateUtils.isLastOfMonth = function isLastOfMonth(date) {
    var d = moment(date);
    var currentMonth = d.month();

    if (d.add(7, "days").month() > currentMonth) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * Fonction permettant de récupérer le libellé du jour
   * @param {date} dateRef La date de référence
   * @returns {date} Le libellé du jour correspondant à _dateRef_
   */
  DateUtils.lastDayOfMonth = function lastDayOfMonth(dateRef) {
    return moment.utc(dateRef).endOf("month").toDate();
  };

  /**
   * Fonction permettant de récupérer la date du dernier jour d'une semaine
   * @param {date} dateRef La date de référence
   * @returns {date} La date du dernier jour de la semaine correspondant à _dateRef_
   */
  DateUtils.lastDayOfWeek = function lastDayOfWeek(dateRef) {
    return moment.utc(dateRef).endOf("week").toDate();
  };

  /**
   * Formatte une chaine date YYYY-MM-DD en chaine DD/MM/YYYY
   * @param {string} date La date au format YYYY-MM-DD
   * @returns {string} La date au format DD/MM/YYYY
   */
  DateUtils.serverDateToDisplayDate = function serverDateToDisplayDate(date) {
    if (!date || date.length < 10) {
      return null;
    }

    return "" + date[8] + date[9] + "/" + date[5] + date[6] + "/" + date[0] + date[1] + date[2] + date[3];
  };

  /**
  * Convertit deux string de type "2020-04-02" et "15:20" en objet DateTime
  * @param {string} stringDate
  * @param {string} stringHour
  * @returns {Date}
  */
  DateUtils.strDateAndTimeToDate = function strDateAndTimeToDate(stringDate, stringHour) {
    // Transformation de l'heure envoyée en string en tableau de int (utilisé ensuite pour générer le DateTime)
    var hourArray = stringHour.split(":"); // Transformation de la date envoyée en string en tableau de int (utilisé ensuite pour générer le DateTime)

    var dateArray = stringDate.split("-");
    return new Date(parseInt(dateArray[0]), parseInt(dateArray[1]) - 1, parseInt(dateArray[2]), parseInt(hourArray[0]), parseInt(hourArray[1]));
  }
  /**
   * Transforme une heure sous forme de chaine hh:mm en heure sous forme numérique
   * @param {string} hour L'heure en chaine sous la form hh:mm
   * @returns {number} L'heure sous forme numérique
   */
  ;

  DateUtils.toHourNumber = function toHourNumber(hour) {
    if (!hour) {
      return 0;
    }

    var result = hour.split(":");
    var minutes = parseInt(result[1]) / 60;
    return Number(result[0]) + minutes;
  };

  /**
   * Conversion de l'heure au format numérique en chaîne
   * @param {number} hour L'heure au format numérique (9.5 pour 9:30)
   * @param {boolean} [duration=false] true pour afficher l'heure au format d'une durée (ex : renvoit 9h00 au lieu de 9:00)
   * @param {boolean} [trunc=false] true pour tronquer l'heure si les minutes valent 0 (ex : renvoit 9h au lieu de 9h00)
   * @param {boolean} [fullFormat=false] true pour renvoyer l'heure compléte : 2 chiffres pour les heures et les minutes
   * @returns {string} L'heure sous forme de chaîne
   */
  DateUtils.toHourString = function toHourString(hourMinute, duration, trunc, fullFormat) {
    if (duration === void 0) {
      duration = false;
    }

    if (trunc === void 0) {
      trunc = false;
    }

    if (fullFormat === void 0) {
      fullFormat = false;
    }

    // Détermination des heures
    var hour = Math.trunc(hourMinute);

    if (!duration && hour >= 24) {
      hour = 0;
    }

    var hourStr = hour.toString();

    if (fullFormat) {
      hourStr = ("0" + hourStr).substring(hourStr.length - 1);
    } // Détermination des minutes


    var minute = Math.round((hourMinute - hour) * 60);

    if (minute >= 60) {
      hour += 1;
      minute -= 60;
    }

    var minuteStr = "0" + minute.toString();
    minuteStr = minuteStr.substring(minuteStr.length - 2); // On retire les minutes en fonction des paramètres

    if (trunc && !fullFormat && minuteStr === "00") {
      minuteStr = "";
    } // Détermination du délimiteur


    var delimiter = duration ? "h" : ":";
    return hourStr + delimiter + minuteStr;
  };

  return DateUtils;
}(); // #endregion
// Exports


export var addDays = DateUtils.addDays;
export var addHours = DateUtils.addHours;
export var addMonth = DateUtils.addMonth;
export var daysBetween = DateUtils.daysBetween;
export var firstDayOfMonth = DateUtils.firstDayOfMonth;
export var firstDayOfWeek = DateUtils.firstDayOfWeek;
export var getDateDDMMYYYY = DateUtils.getDateDDMMYYYY;
export var getDateHeure = DateUtils.getDateHeure;
export var getDateYYYYMMDD = DateUtils.getDateYYYYMMDD;
export var getDayName = DateUtils.getDayName;
export var initLocaleMoment = DateUtils.initLocaleMoment;
export var isDateValid = DateUtils.isDateValid;
export var isLastOfMonth = DateUtils.isLastOfMonth;
export var lastDayOfMonth = DateUtils.lastDayOfMonth;
export var lastDayOfWeek = DateUtils.lastDayOfWeek;
export var serverDateToDisplayDate = DateUtils.serverDateToDisplayDate;
export var strDateAndTimeToDate = DateUtils.strDateAndTimeToDate;
export var toHourNumber = DateUtils.toHourNumber;
export var toHourString = DateUtils.toHourString;